import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ClockIcon, ExternalLinkIcon, MapPinIcon, MessageSquareIcon, NavigationIcon, PhoneIcon } from 'lucide-react';
import { LOCATIONS, SERVICE_MENUS } from '../data/locations';
function toIntlNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? `+61${digits.slice(1)}` : digits;
}
export function LocationDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const location = LOCATIONS.find(l => l.slug === slug);
  if (!location) {
    return <Navigate to="/locations" replace data-id="element-264" data-mp="29x75d0tjg7i9" />;
  }
  const mapQuery = encodeURIComponent(`${location.name}, ${location.address}`);
  const smsBody = `Hi ${location.name}, I'd like to book a massage. Sent via the Me Time website.`;
  const others = LOCATIONS.filter(l => l.slug !== location.slug).slice(0, 3);
  return <main className="w-full bg-black" data-id="element-265" data-mp="36poxeh6bpsp">
      <section className="relative w-full overflow-hidden" data-id="element-266" data-mp="2d4agmwf36wxc">
        <img src={location.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-267" data-mp="2d2po1ccc1o24" />
        <div className="absolute inset-0 bg-black/70" data-id="element-268" data-mp="244dyzer46ybs" />
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-24" data-id="element-269" data-mp="292fig0sgczns">
          <Link to="/locations" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-lotus-500" data-id="element-270" data-mp="30sbos48csbc8">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-271" data-mp="2s11lvmbfz2uv" />
            All locations
          </Link>
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.28em] text-lotus-500" data-id="element-272" data-mp="3fqcgem27810k">
            {location.suburb}
          </p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-id="element-273" data-mp="3kj5kr8tr97c4">
            {location.name}
          </h1>
          <p className="mt-4 flex items-center gap-2 text-neutral-300" data-id="element-274" data-mp="3pehxuykz9mjv">
            <MapPinIcon className="h-5 w-5 text-lotus-500" aria-hidden="true" data-id="element-275" data-mp="3pcf01sdl2p4l" />
            {location.address}
          </p>
        </motion.div>
      </section>

      <section aria-label="Venue details" className="w-full bg-black" data-id="element-276" data-mp="3ffabjlw9mhkm">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_360px]" data-id="element-277" data-mp="3begosvuk78tb">
          <div className="space-y-10" data-id="element-278" data-mp="36ngp5iu22cn4">
            <div className="overflow-hidden rounded-2xl border border-white/5" data-id="element-279" data-mp="36mfgtm4fc493">
              <iframe title={`Map showing ${location.name}`} src={`https://maps.google.com/maps?q=${mapQuery}&z=15&output=embed`} loading="lazy" className="h-[320px] w-full border-0 grayscale-[0.4]" referrerPolicy="no-referrer-when-downgrade" data-id="element-280" data-mp="31pyqcl9r5dsu" />
            </div>

            <div data-id="element-281" data-mp="1373odwicdrxn">
              <h2 className="text-2xl font-bold tracking-tight text-white" data-id="element-282" data-mp="137my4pxu9a0l">
                Treatments at {location.suburb}
              </h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-283" data-mp="17745jowtsh8e">
                {SERVICE_MENUS.map(menu => <section key={menu.name} aria-label={menu.name} className="rounded-2xl border border-white/5 bg-ink-850 p-6" data-id="element-284" data-mp="1c5p3tisioqu7">
                    <h3 className="text-lg font-bold text-white" data-id="element-285" data-mp="jh9qd0p1agkz">{menu.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-286" data-mp="oigxqyn9ldr4">
                      {menu.description}
                    </p>
                    <ul className="mt-5 divide-y divide-white/5" data-id="element-287" data-mp="y8ioxzawzca9">
                      {menu.tiers.map(tier => <li key={tier.duration} className="flex items-baseline justify-between gap-4 py-3" data-id="element-288" data-mp="rlckkdbdgsu">
                          <span className="text-sm text-neutral-300" data-id="element-289" data-mp="27gdh398g7ewm">{tier.duration}</span>
                          <span className="text-xl font-extrabold text-lotus-500" data-id="element-290" data-mp="23ftomiytkzgt">
                            {tier.price}
                          </span>
                        </li>)}
                    </ul>
                  </section>)}
              </div>
            </div>

            <div data-id="element-291" data-mp="2r434l49cqt01">
              <h2 className="text-2xl font-bold tracking-tight text-white" data-id="element-292" data-mp="2r3jjxfsbhqib">Other venues nearby</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-3" data-id="element-293" data-mp="2m4zyrhljsir0">
                {others.map(other => <Link key={other.slug} to={`/locations/${other.slug}`} className="rounded-xl border border-white/5 bg-ink-850 p-5 transition-colors hover:border-lotus-500/50" data-id="element-294" data-mp="305100y5q5s70">
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-295" data-mp="10syhbq4xd1ue">
                      {other.suburb}
                    </span>
                    <span className="mt-2 block font-semibold text-white" data-id="element-296" data-mp="3ci4sbsm0i1u3">{other.name}</span>
                  </Link>)}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start" data-id="element-297" data-mp="33ldw9g7gmmzi">
            <div className="rounded-2xl border border-white/5 bg-ink-900 p-6" data-id="element-298" data-mp="37nr182edugv8">
              <h2 className="text-lg font-bold text-white" data-id="element-299" data-mp="2tp13qlnnif6c">Book at this venue</h2>
              <ul className="mt-5 space-y-3.5 text-sm text-neutral-300" data-id="element-300" data-mp="2yo41dl1axj4a">
                <li className="flex items-start gap-3" data-id="element-301" data-mp="2ym0za2mvjzj7">
                  <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-302" data-mp="2kpnqrj0bj8gv" />
                  {location.address}
                </li>
                <li className="flex items-start gap-3" data-id="element-303" data-mp="ixr6nl034qah">
                  <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-304" data-mp="mzcpro37q0t4" />
                  <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="hover:text-lotus-500" data-id="element-305" data-mp="1vtorehurteoy">
                    {location.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3" data-id="element-306" data-mp="1hwjrkzlnhc25">
                  <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-307" data-mp="1hy37uq7b3n3x" />
                  {location.hours}
                </li>
              </ul>

              <div className="mt-6 space-y-3" data-id="element-308" data-mp="25tfliiff5roi">
                {location.squareBookingUrl ? <a href={location.squareBookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-lotus-500 px-6 py-3.5 font-semibold text-black transition-colors hover:bg-lotus-600" data-id="element-309" data-mp="20v4esgkf7xai">
                    Choose a time
                    <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-310" data-mp="35tj1xqpzbiwm" />
                  </a> : <a href={`sms:${toIntlNumber(location.phone)}?&body=${encodeURIComponent(smsBody)}`} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-lotus-500 px-6 py-3.5 font-semibold text-black transition-colors hover:bg-lotus-600" data-id="element-311" data-mp="3asm7dc9vfr54">
                    <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-312" data-mp="3tjlsu3i5qitn" />
                    Text to book
                  </a>}
                <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-ink-850 px-6 py-3.5 font-semibold text-white transition-colors hover:border-white/25" data-id="element-313" data-mp="2cj2fea58gkh">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-314" data-mp="wmulrdmval8s" />
                  Call venue
                </a>
                <a href={`https://maps.google.com/maps?q=${mapQuery}`} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-ink-850 px-6 py-3.5 font-semibold text-white transition-colors hover:border-white/25" data-id="element-315" data-mp="rryqm4mc3hrg">
                  <NavigationIcon className="h-4 w-4" aria-hidden="true" data-id="element-316" data-mp="8x0ah0i754qn" />
                  Get directions
                </a>
              </div>

              <p className="mt-5 text-xs leading-relaxed text-neutral-500" data-id="element-317" data-mp="ix0ukonw3ovw">
                Walk-ins welcome when a therapist is free. Services for men only, ages 18+.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>;
}